import { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardActionArea, CardContent, Checkbox,
  CircularProgress, Divider, Grid, TextField, Typography
} from '@mui/material';
import { Device, DeviceRepair } from '../../Utils/types';
import { getData } from '../../Services/getData';
import { UrlEnum } from '../../Utils/Utils';
import { Vocabulary } from '../../Utils/Vocabulary';

interface Props {
  device: Device;
  onContinue: (data: { selectedRepairs: DeviceRepair[]; existingRepairDetails: string; otherProblemDescription: string }) => void;
  onBack: () => void;
  /**
   * Optional id of a repair to pre-check on load (e.g. from the repairs
   * catalog shortcode's deep link). Seeds the initial selection only — once
   * the user toggles anything, selection continues to flow through the
   * normal toggleRepair/history-state path untouched.
   */
  preselectRepairId?: string;
}

export default function StepRepairs({ device, onContinue, onBack, preselectRepairId }: Props) {
  const [repairs, setRepairs] = useState<DeviceRepair[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [existingDetails, setExistingDetails] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getData(`${UrlEnum.publicDeviceRepairs}/${device._id}`).then((res) => {
      if (res?.data) {
        setRepairs(res.data);
        if (preselectRepairId && res.data.some((r: DeviceRepair) => r._id === preselectRepairId)) {
          setSelectedIds(new Set([preselectRepairId]));
        }
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device._id]);

  function toggleRepair(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const canContinue = selectedIds.size > 0 || otherDescription.trim().length > 0;

  function handleContinue() {
    if (!canContinue) {
      setError(Vocabulary.selectAtLeastOne);
      return;
    }
    setError('');
    onContinue({
      selectedRepairs: repairs.filter((r) => selectedIds.has(r._id)),
      existingRepairDetails: existingDetails,
      otherProblemDescription: otherDescription,
    });
  }

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{Vocabulary.stepRepairs}</Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {Vocabulary.stepRepairsSubtitle}
      </Typography>

      {repairs.length === 0 ? (
        <Typography color="text.secondary" mb={2}>{Vocabulary.noRepairsFound}</Typography>
      ) : (
        <Grid container spacing={2} mb={3}>
          {repairs.map((repair) => {
            const isSelected = selectedIds.has(repair._id);
            return (
              <Grid item xs={12} sm={6} md={4} key={repair._id}>
                <Card
                  elevation={isSelected ? 6 : 2}
                  sx={{
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    borderRadius: 2,
                  }}
                >
                  <CardActionArea onClick={() => toggleRepair(repair._id)}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Checkbox checked={isSelected} onChange={() => {}} size="small" sx={{ mr: 1, p: 0 }} />
                        <Typography variant="subtitle1" fontWeight={600}>{repair.repairName}</Typography>
                      </Box>
                      {repair.spareParts.map((p) => (
                        <Typography key={p.name} variant="body2" color="text.secondary">
                          {p.name}: {p.price} {Vocabulary.ron}
                        </Typography>
                      ))}
                      <Typography variant="body1" fontWeight={600} mt={1}>
                        {Vocabulary.totalPrice}: {repair.totalPrice} {Vocabulary.ron}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <TextField
        label={Vocabulary.existingRepairDetails}
        helperText={Vocabulary.existingRepairDetailsHint}
        multiline
        rows={3}
        fullWidth
        value={existingDetails}
        onChange={(e) => setExistingDetails(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 3 }} />

      <Typography variant="h6" gutterBottom>{Vocabulary.otherProblem}</Typography>
      <TextField
        label={Vocabulary.otherProblemDescription}
        multiline
        rows={3}
        fullWidth
        value={otherDescription}
        onChange={(e) => setOtherDescription(e.target.value)}
        sx={{ mb: 2 }}
      />

      {error && <Typography color="error" mb={2}>{error}</Typography>}

      <Box display="flex" justifyContent="space-between" mt={2}>
        <Button variant="outlined" onClick={onBack}>{Vocabulary.back}</Button>
        <Button variant="contained" onClick={handleContinue} disabled={!canContinue}>
          {Vocabulary.continue}
        </Button>
      </Box>
    </Box>
  );
}
